import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  ShieldAlert,
  CheckCircle2,
  FileText,
  Layers,
  Filter,
  AlertTriangle,
  ClipboardList,
  CalendarDays,
  ChevronRight,
  ChevronDown,
  Target,
  Clock,
  MapPin,
  Building2,
  Info,
  FolderKanban
} from 'lucide-react';
import DataTable from './DataTable';
import Badge from './common/Badge';
import Button from './common/Button';
import { useAppContext } from '../context/AppContext';
import { formatDate } from '../utils/helpers';
import { t } from '../i18n';

// Tonos semánticos para el estado del hito y el semáforo de avance.
const STATUS_TONE = {
  pendiente: 'neutral',
  en_curso: 'warning',
  en_desarrollo: 'info',
  enviado: 'info',
  completado: 'success'
};

const SEMAPHORE_TONE = {
  success: 'success',
  progress: 'warning',
  pending: 'neutral',
  warning: 'warning',
  critical: 'danger'
};

const projectColorClass = (color) => {
  switch (color) {
    case 'emerald':
      return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    case 'sky':
      return 'bg-sky-100 text-sky-700 border border-sky-200';
    case 'amber':
      return 'bg-amber-100 text-amber-700 border border-amber-200';
    default:
      return 'bg-slate-100 text-slate-600 border border-slate-200';
  }
};

const controlTypeText = {
  permiso: 'Permiso / Autorización',
  contrato: 'Contrato / Acuerdo',
  estudio: 'Estudio / Informe',
  gestión: 'Gestión'
};

const dayFormatter = new Intl.DateTimeFormat('es-CL', { dateStyle: 'medium' });

const toDateInputValue = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatDisplayDate = (value) => {
  if (!value) return '—';
  const input = toDateInputValue(value);
  if (!input) return '—';
  return formatDate(input);
};

const getProgressColor = (value) => {
  if (value >= 80) return 'bg-success-500';
  if (value >= 50) return 'bg-warning-500';
  return 'bg-danger-500';
};

const defaultFilters = {
  stage: 'all',
  category: 'all',
  status: 'all',
  responsible: 'all',
  permitsOnly: false,
  criticalOnly: false
};

const ProjectStatus = () => {
  const {
    projectStatusProjects,
    projectStatusCatalog,
    projectStageOrder,
    getProjectStatusBundle,
    updateProjectStatusItem,
    resetProjectStatusMatrix,
    pushNotification
  } = useAppContext();

  const defaultProjectId = projectStatusProjects[0]?.id || null;
  const [selectedProjectId, setSelectedProjectId] = useState(defaultProjectId);
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [openCategories, setOpenCategories] = useState(() => new Set());
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [insightPanel, setInsightPanel] = useState(null);

  const projectBundle = getProjectStatusBundle(selectedProjectId);
  const { project, items: projectItems, summary, stages, categories } = projectBundle;

  const statusOptions = useMemo(
    () =>
      Object.entries(projectStatusCatalog).map(([key, value]) => ({
        value: key,
        label: value.label
      })),
    [projectStatusCatalog]
  );

  const categoryOptions = useMemo(
    () => categories.map((category) => category.name),
    [categories]
  );

  const responsibles = useMemo(() => {
    const set = new Set(projectItems.map((item) => item.responsible));
    return Array.from(set).sort();
  }, [projectItems]);

  const stagesWithData = useMemo(() => stages.map((stage) => stage.stage), [stages]);

  const filteredItems = useMemo(() => {
    return projectItems
      .filter((item) => (filters.stage === 'all' ? true : item.stage === filters.stage))
      .filter((item) => (filters.category === 'all' ? true : item.category === filters.category))
      .filter((item) => (filters.status === 'all' ? true : item.status === filters.status))
      .filter((item) => (filters.responsible === 'all' ? true : item.responsible === filters.responsible))
      .filter((item) => (filters.permitsOnly ? item.requiresPermit : true))
      .filter((item) => (filters.criticalOnly ? item.semaphore === 'critical' : true));
  }, [projectItems, filters]);

  const tableData = useMemo(
    () =>
      filteredItems.map((item) => ({
        ...item,
        expectedDateLabel: formatDisplayDate(item.expectedDate),
        actualDateLabel: formatDisplayDate(item.actualDate)
      })),
    [filteredItems]
  );

  const selectedItem = useMemo(
    () => projectItems.find((item) => item.id === selectedItemId) || null,
    [projectItems, selectedItemId]
  );

  const [editState, setEditState] = useState({
    status: 'pendiente',
    expectedDate: '',
    actualDate: '',
    document: '',
    notes: ''
  });

  useEffect(() => {
    if (!selectedItem) return;
    setEditState({
      status: selectedItem.status,
      expectedDate: toDateInputValue(selectedItem.expectedDate),
      actualDate: toDateInputValue(selectedItem.actualDate),
      document: selectedItem.document || '',
      notes: selectedItem.notes || ''
    });
  }, [selectedItem]);

  useEffect(() => {
    setFilters(defaultFilters);
    setSelectedItemId(null);
    setOpenCategories(new Set());
    setInsightPanel(null);
  }, [selectedProjectId]);

  const buildInsight = (type, overrideFilters = null) => {
    switch (type) {
      case 'overall':
        return {
          id: 'overall',
          title: t('Detalle de avance global'),
          description:
            t(
              'Listado consolidado de todos los hitos del proyecto ordenados por estado y prioridad.'
            ),
          items: [...projectItems].sort((a, b) => b.progressWeight - a.progressWeight),
          filters: overrideFilters || null
        };
      case 'permits':
        return {
          id: 'permits',
          title: t('Permisos y autorizaciones'),
          description:
            t(
              'Incluye trámites con SEC, SEREMI, MOP y entidades sectoriales que habilitan la construcción.'
            ),
          items: projectItems
            .filter((item) => item.requiresPermit)
            .sort((a, b) => (a.expectedDate || '').localeCompare(b.expectedDate || '')),
          filters: overrideFilters || { permitsOnly: true }
        };
      case 'contracts':
        return {
          id: 'contracts',
          title: t('Contratos críticos'),
          description:
            t(
              'Contratos EPC, O&M, PPA y acuerdos financieros con impacto directo en el CAPEX.'
            ),
          items: projectItems
            .filter((item) => item.isContract)
            .sort((a, b) => (a.expectedDate || '').localeCompare(b.expectedDate || '')),
          filters: overrideFilters
        };
      case 'alerts':
        return {
          id: 'alerts',
          title: t('Alertas críticas'),
          description:
            t(
              'Hitos en color rojo o vencidos. Requieren atención inmediata del responsable designado.'
            ),
          items: projectItems
            .filter((item) => item.semaphore === 'critical' || item.status === 'pendiente')
            .sort((a, b) => (a.expectedDate || '').localeCompare(b.expectedDate || '')),
          filters: overrideFilters || { criticalOnly: true }
        };
      default:
        return null;
    }
  };

  const handleCardClick = (type) => {
    const insight = buildInsight(type);
    if (insight?.filters) {
      setFilters((prev) => ({ ...prev, ...insight.filters }));
    }
    setInsightPanel(insight);
  };

  const stageTimeline = useMemo(() => {
    if (!stages || stages.length === 0) return [];
    const baseStart = new Date('2024-01-08T00:00:00');
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    let cursor = new Date(baseStart);
    const segments = stages.map((entry) => {
      const durationWeeks = Math.max(1, Math.ceil(Math.max(entry.summary.total, 1) / 3));
      const startDate = new Date(cursor);
      const endDate = new Date(cursor.getTime() + durationWeeks * weekMs);
      cursor = new Date(endDate.getTime());
      return {
        ...entry,
        startDate,
        endDate,
        durationWeeks
      };
    });
    const totalSpan = Math.max(segments[segments.length - 1].endDate - baseStart, weekMs);
    return segments.map((segment) => {
      const offsetMs = segment.startDate - baseStart;
      const widthMs = Math.max(segment.endDate - segment.startDate, weekMs);
      const rawWidthPercent = Math.max((widthMs / totalSpan) * 100, 6);
      const offsetPercent = Math.min(Math.max((offsetMs / totalSpan) * 100, 0), 100);
      const availablePercent = Math.max(0, 100 - offsetPercent);
      const widthPercent = Math.max(Math.min(availablePercent, rawWidthPercent), 4);
      return {
        ...segment,
        widthPercent,
        offsetPercent,
        rangeLabel: `${dayFormatter.format(segment.startDate)} → ${dayFormatter.format(
          segment.endDate
        )}`
      };
    });
  }, [stages]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggle = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleProjectChange = (event) => {
    const newProjectId = event.target.value || null;
    setSelectedProjectId(newProjectId);
  };

  const handleSave = () => {
    if (!selectedItem) return;
    const payload = {
      status: editState.status,
      expectedDate: editState.expectedDate || null,
      actualDate: editState.actualDate || null,
      document: editState.document,
      notes: editState.notes
    };
    updateProjectStatusItem(selectedItem.id, payload);
    setFeedbackMessage(t('Avance actualizado correctamente.'));
    pushNotification({
      title: t('Hito actualizado'),
      message: t(
        '{0} quedó en estado {1}.',
        selectedItem.name,
        projectStatusCatalog[payload.status]?.label || payload.status
      ),
      severity: 'info'
    });
    setTimeout(() => setFeedbackMessage(''), 2500);
  };

  const handleQuickComplete = () => {
    if (!selectedItem) return;
    const today = new Date().toISOString().slice(0, 10);
    const payload = {
      status: 'completado',
      actualDate: today,
      expectedDate: editState.expectedDate || selectedItem.expectedDate || null
    };
    updateProjectStatusItem(selectedItem.id, payload);
    setEditState((prev) => ({
      ...prev,
      status: 'completado',
      actualDate: today
    }));
    pushNotification({
      title: t('Hito completado'),
      message: t('{0} fue marcado como completado.', selectedItem.name),
      severity: 'success'
    });
    setFeedbackMessage(t('Hito marcado como completado.'));
    setTimeout(() => setFeedbackMessage(''), 2500);
  };

  const handleCategoryToggle = (categoryName) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  const categoryLegend = [
    { label: t('Completado'), className: 'bg-success-500' },
    { label: t('En curso'), className: 'bg-warning-500' },
    { label: t('Pendiente'), className: 'bg-neutral-300' },
    { label: t('Crítico'), className: 'bg-danger-500' }
  ];

  const computeCategorySegments = (categorySummary) => {
    const total = Math.max(categorySummary.total, 1);
    return [
      { label: t('Completado'), value: categorySummary.completed, className: 'bg-success-500' },
      { label: t('En curso'), value: categorySummary.inProgress, className: 'bg-warning-500' },
      { label: t('Pendiente'), value: categorySummary.pending, className: 'bg-neutral-300' },
      { label: t('Crítico'), value: categorySummary.critical, className: 'bg-danger-500' }
    ]
      .filter((segment) => segment.value > 0)
      .map((segment) => ({
        ...segment,
        width: Math.max((segment.value / total) * 100, 5)
      }));
  };

  const tableColumns = useMemo(
    () => [
      { key: 'id', label: 'ID', sortable: true },
      { key: 'stage', label: t('Etapa'), sortable: true },
      { key: 'category', label: t('Categoría'), sortable: true },
      { key: 'name', label: t('Nombre'), sortable: true },
      {
        key: 'statusLabel',
        label: t('Estado'),
        sortable: true,
        render: (value, row) => <Badge tone={STATUS_TONE[row.status] || 'neutral'}>{t(value)}</Badge>
      },
      { key: 'responsible', label: t('Responsable'), sortable: true },
      {
        key: 'expectedDate',
        label: t('Fecha Esperada'),
        sortable: true,
        render: (_, row) => row.expectedDateLabel
      },
      {
        key: 'actualDate',
        label: t('Fecha Real'),
        sortable: true,
        render: (_, row) => row.actualDateLabel
      },
      {
        key: 'progressWeight',
        label: t('Avance'),
        sortable: true,
        render: (value) => (
          <div className="flex items-center gap-3">
            <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${getProgressColor(value)}`}
                style={{ width: `${Math.min(100, Math.max(value, 0))}%` }}
              />
            </div>
            <span className="text-xs text-slate-600 font-semibold">{t(value)}%</span>
          </div>
        )
      },
      {
        key: 'semaphore',
        label: t('Semáforo'),
        render: (value) => (
          <Badge tone={SEMAPHORE_TONE[value] || SEMAPHORE_TONE.pending}>
            {value === 'critical'
              ? t('Crítico')
              : value === 'warning'
              ? t('Atención')
              : value === 'progress'
              ? t('En curso')
              : value === 'success'
              ? 'OK'
              : t('Pendiente')}
          </Badge>
        )
      }
    ],
    []
  );

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Layers className="w-7 h-7 text-neutral-400" />{t('Status de Proyectos')}</h1>
            <p className="text-sm text-slate-500 max-w-2xl">{t(
              'Monitorea permisos, contratos y entregables clave de los proyectos fotovoltaicos. Selecciona un proyecto para revisar el avance por etapa, categoría y responsables.'
            )}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-4 py-2 shadow-sm">
              <select
                value={selectedProjectId || ''}
                onChange={handleProjectChange}
                className="bg-transparent text-sm font-semibold text-slate-700 outline-none"
              >
                {projectStatusProjects.map((proj) => (
                  <option key={proj.id} value={proj.id}>
                    {t(proj.name)}
                  </option>
                ))}
                {projectStatusProjects.length > 1 && (
                  <option value="">{t('Visión global')}</option>
                )}
              </select>
            </div>
            <Button variant="secondary" onClick={() => setIsResetConfirmOpen(true)}>{t('Restaurar matriz base')}</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <button
            type="button"
            className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm text-left hover:border-neutral-300 transition"
            onClick={() => handleCardClick('overall')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('Avance global')}</p>
                <h3 className="text-2xl font-bold text-slate-900">{t(summary.overallProgress) || 0}%</h3>
              </div>
              <BarChart3 className="w-5 h-5 text-neutral-400" />
            </div>
            <div className="mt-4">
              <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${getProgressColor(summary.overallProgress || 0)}`}
                  style={{ width: `${summary.overallProgress || 0}%` }}
                />
              </div>
              <p className="mt-3 text-xs text-slate-500">
                {t(summary.completed)} {t('completados ·')} {t(summary.inProgress)} {t('en curso ·')} {t(summary.pending)}{t('pendientes')}</p>
            </div>
          </button>

          <button
            type="button"
            className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm text-left hover:border-neutral-300 transition"
            onClick={() => handleCardClick('permits')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('Permisos')}</p>
                <h3 className="text-2xl font-bold text-slate-900">{t(summary.permits)}</h3>
              </div>
              <FileText className="w-5 h-5 text-neutral-400" />
            </div>
            <p className="mt-3 text-xs text-slate-500">{t('Autorizaciones SEREMI, SEC, MOP y sectoriales priorizadas.')}</p>
          </button>

          <button
            type="button"
            className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm text-left hover:border-neutral-300 transition"
            onClick={() => handleCardClick('contracts')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('Contratos')}</p>
                <h3 className="text-2xl font-bold text-slate-900">{t(summary.contracts)}</h3>
              </div>
              <ClipboardList className="w-5 h-5 text-neutral-400" />
            </div>
            <p className="mt-3 text-xs text-slate-500">{t('EPC, O&M, PPA y acuerdos financieros con impacto directo en el CAPEX.')}</p>
          </button>

          <button
            type="button"
            className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm text-left hover:border-neutral-300 transition"
            onClick={() => handleCardClick('alerts')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('Alertas críticas')}</p>
                <h3 className="text-2xl font-bold text-danger-600">{t(summary.critical)}</h3>
              </div>
              <ShieldAlert className="w-5 h-5 text-danger-500" />
            </div>
            <p className="mt-3 text-xs text-slate-500">{t('Entregables con semáforo rojo o fuera de plazo comprometido.')}</p>
          </button>
        </div>

        {project && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 border ${projectColorClass(project.color)} bg-white`}>
              <Info className="w-4 h-4" />
              <div>
                <p className="text-xs uppercase tracking-wide font-semibold">{t('Proyecto')}</p>
                <p className="text-sm font-semibold">{t(project.name)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl px-4 py-3 border border-slate-200 bg-white">
              <MapPin className="w-4 h-4 text-slate-500" />
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{t('Ubicación')}</p>
                <p className="text-sm font-semibold text-slate-700">{t(project.location) || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl px-4 py-3 border border-slate-200 bg-white">
              <Building2 className="w-4 h-4 text-slate-500" />
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{t('Capacidad estimada')}</p>
                <p className="text-sm font-semibold text-slate-700">
                  {project.capacityMw ? `${project.capacityMw} MW` : '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-neutral-400" />{t('Roadmap y Gantt de etapas')}</h2>
            <p className="text-sm text-slate-500">{t('Visualiza el avance promedio por etapa y los hitos críticos asociados.')}</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full bg-success-500" />{t('En objetivo')}</div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full bg-warning-500" />{t('En riesgo')}</div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full bg-danger-500" />{t('Atrasado')}</div>
          </div>
        </div>

        <div className="space-y-5">
          {stageTimeline.map((segment) => (
            <div key={`${segment.stage}-${segment.rangeLabel}`} className="space-y-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                  <p className="text-sm font-semibold text-slate-800 uppercase tracking-wide">
                    {t(segment.stage)}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="w-4 h-4" />
                    {t(segment.rangeLabel)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {t(segment.durationWeeks)} {t('semana(s)')}</span>
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    {t(segment.summary.completed)}/{t(segment.summary.total)} {t('completados')}</span>
                  {segment.summary.critical > 0 && (
                    <span className="inline-flex items-center gap-1 text-danger-600 font-semibold">
                      <AlertTriangle className="w-4 h-4" />
                      {t(segment.summary.critical)} {t('alerta(s)')}</span>
                  )}
                </div>
              </div>
              <div className="relative h-10 rounded-xl bg-slate-100 overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full rounded-xl"
                  style={{
                    width: `${segment.widthPercent}%`,
                    marginLeft: `${segment.offsetPercent}%`,
                    background:
                      segment.summary.overallProgress >= 80
                        ? 'rgba(31, 157, 85, 0.18)'
                        : segment.summary.overallProgress >= 50
                        ? 'rgba(183, 121, 31, 0.18)'
                        : 'rgba(192, 54, 44, 0.18)',
                    border: '1px solid rgba(148, 163, 184, 0.25)'
                  }}
                >
                  <div
                    className={`h-full ${getProgressColor(segment.summary.overallProgress)}`}
                    style={{ width: `${segment.summary.overallProgress}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-slate-500" />
            <h2 className="text-lg font-semibold text-slate-900">{t('Resumen por categoría')}</h2>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            {categoryLegend.map((item) => (
              <span key={item.label} className="inline-flex items-center gap-1">
                <span className={`inline-block w-3 h-3 rounded ${item.className}`} />
                {t(item.label)}
              </span>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          {categories.map((category) => {
            const segments = computeCategorySegments(category.summary);
            const isOpen = openCategories.has(category.name);
            return (
              <div
                key={category.name}
                className="border border-slate-200 rounded-2xl p-4 bg-white shadow-sm space-y-3"
              >
                <button
                  type="button"
                  onClick={() => handleCategoryToggle(category.name)}
                  className="flex w-full items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 font-semibold">
                      {t(category.name.slice(0, 2).toUpperCase())}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-900">{t(category.name)}</p>
                      <p className="text-xs text-slate-500">
                        {t(category.summary.completed)} {t('completados ·')} {t(category.summary.pending)}{t('pendientes')}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-0.5 h-3 rounded-full overflow-hidden bg-slate-100 w-40">
                      {segments.map((segment) => (
                        <span
                          key={`${category.name}-${segment.label}`}
                          className={segment.className}
                          style={{ width: `${segment.width}%` }}
                          title={`${segment.label}: ${segment.value}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>{t(category.summary.overallProgress)}%</span>
                      <ChevronDown
                        className={`w-4 h-4 text-slate-500 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>
                </button>
                {isOpen && (
                    <div className="space-y-3">
                      {category.stages.map((stageEntry) => (
                        <div
                          key={`${category.name}-${stageEntry.stage}`}
                          className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-2"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                              <p className="text-sm font-semibold text-slate-700">{t(stageEntry.stage)}</p>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span>
                                {t(stageEntry.summary.completed)}/{t(stageEntry.summary.total)} {t('completados')}</span>
                              <span>{t(stageEntry.summary.overallProgress)}{t('% avance')}</span>
                            </div>
                          </div>
                          <div className="relative h-2 bg-white border border-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`absolute top-0 left-0 h-full ${getProgressColor(stageEntry.summary.overallProgress)}`}
                              style={{ width: `${stageEntry.summary.overallProgress}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <p className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{t('Filtros dinámicos')}</p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setFilters(defaultFilters)}>{t('Limpiar')}</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-semibold uppercase">{t('Etapa')}</label>
            <select
              value={filters.stage}
              onChange={(event) => handleFilterChange('stage', event.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
            >
              <option value="all">{t('Todas')}</option>
              {projectStageOrder.map((stage) => (
                <option key={stage} value={stage} disabled={!stagesWithData.includes(stage)}>
                  {t(stage)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-semibold uppercase">{t('Categoría')}</label>
            <select
              value={filters.category}
              onChange={(event) => handleFilterChange('category', event.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
            >
              <option value="all">{t('Todas')}</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {t(category)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-semibold uppercase">{t('Estado')}</label>
            <select
              value={filters.status}
              onChange={(event) => handleFilterChange('status', event.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
            >
              <option value="all">{t('Todos')}</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-semibold uppercase">{t('Responsable')}</label>
            <select
              value={filters.responsible}
              onChange={(event) => handleFilterChange('responsible', event.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
            >
              <option value="all">{t('Todos')}</option>
              {responsibles.map((responsible) => (
                <option key={responsible} value={responsible}>
                  {t(responsible)}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.permitsOnly}
              onChange={() => handleToggle('permitsOnly')}
              className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />{t('Solo permisos / autorizaciones')}</label>
          <label className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.criticalOnly}
              onChange={() => handleToggle('criticalOnly')}
              className="rounded border-slate-300 text-danger-600 focus:ring-danger-500"
            />{t('Sólo semáforos críticos')}</label>
        </div>
      </div>

      <div className="space-y-4">
        <DataTable
          data={tableData}
          columns={tableColumns}
          searchPlaceholder={t('Buscar hitos, permisos o contratos...')}
          onRowClick={(row) => setSelectedItemId(row.id)}
          selectedRowId={selectedItemId}
        />

        {selectedItem ? (
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">{t('Hito seleccionado')}</p>
                  <h3 className="text-lg font-semibold text-slate-900 leading-tight">
                    {t(selectedItem.name)}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {t(selectedItem.stage)} · {t(selectedItem.category)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedItemId(null)}
                  className="text-xs text-slate-400 hover:text-slate-600 transition font-semibold"
                >{t('Cerrar')}</button>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Badge tone={STATUS_TONE[selectedItem.status] || 'neutral'}>
                  {t(selectedItem.statusLabel)}
                </Badge>
                <Badge tone={SEMAPHORE_TONE[selectedItem.semaphore] || SEMAPHORE_TONE.pending}>
                  {selectedItem.semaphore === 'critical'
                    ? t('Crítico')
                    : selectedItem.semaphore === 'warning'
                    ? t('Atención')
                    : selectedItem.semaphore === 'progress'
                    ? t('En curso')
                    : selectedItem.semaphore === 'success'
                    ? t('En verde')
                    : t('Pendiente')}
                </Badge>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-slate-500 font-semibold uppercase">{t('Estado del hito')}</label>
                  <select
                    value={editState.status}
                    onChange={(event) =>
                      setEditState((prev) => ({ ...prev, status: event.target.value }))
                    }
                    className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.label)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-slate-500 font-semibold uppercase">{t('Documento / referencia')}</label>
                  <input
                    type="text"
                    value={editState.document}
                    onChange={(event) =>
                      setEditState((prev) => ({ ...prev, document: event.target.value }))
                    }
                    placeholder={t('Ruta en drive, ID documento o enlace')}
                    className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-slate-500 font-semibold uppercase">{t('Fecha esperada')}</label>
                  <input
                    type="date"
                    value={editState.expectedDate}
                    onChange={(event) =>
                      setEditState((prev) => ({ ...prev, expectedDate: event.target.value }))
                    }
                    className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-slate-500 font-semibold uppercase">{t('Fecha real')}</label>
                  <input
                    type="date"
                    value={editState.actualDate}
                    onChange={(event) =>
                      setEditState((prev) => ({ ...prev, actualDate: event.target.value }))
                    }
                    className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2">
                <label className="text-xs text-slate-500 font-semibold uppercase">{t('Observaciones')}</label>
                <textarea
                  value={editState.notes}
                  onChange={(event) =>
                    setEditState((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  rows={4}
                  className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
                  placeholder={t('Comentarios relevantes, dependencias, bloqueos, etc.')}
                />
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button variant="primary" icon={CheckCircle2} onClick={handleSave}>{t('Guardar actualización')}</Button>
                <Button variant="secondary" icon={AlertTriangle} onClick={handleQuickComplete}>{t('Marcar como completado')}</Button>
                <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                  <p className="font-semibold text-slate-600 mb-1">{t('Tipo de control')}</p>
                  <p>{t(controlTypeText[selectedItem.controlType]) || t('Gestión')}</p>
                  <p className="mt-2">{t('Responsable:')}{' '}
                    <span className="font-semibold">{t(selectedItem.responsible)}</span>
                  </p>
                </div>
              </div>

              {feedbackMessage && (
                <div className="mt-4 text-xs font-semibold text-success-600 bg-success-50 border border-success-100 rounded-xl px-3 py-2">
                  {t(feedbackMessage)}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-center text-sm text-slate-500">{t(
              'Selecciona un registro de la tabla para revisar detalles, actualizar fechas y dejar observaciones.'
            )}</div>
          )}
      </div>

      {insightPanel && (
          <div className="fixed bottom-6 right-6 z-40 w-full max-w-lg">
            <div className="bg-white border border-slate-200 shadow-2xl rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('Panel informativo')}</p>
                  <h3 className="text-lg font-semibold text-slate-900">{t(insightPanel.title)}</h3>
                  <p className="text-xs text-slate-500 mt-1">{t(insightPanel.description)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setInsightPanel(null)}
                  className="text-xs text-slate-400 hover:text-slate-600 font-semibold"
                >{t('Cerrar')}</button>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {insightPanel.items.length === 0 ? (
                  <div className="px-5 py-6 text-sm text-slate-500">{t('No hay elementos asociados a esta vista.')}</div>
                ) : (
                  insightPanel.items.slice(0, 12).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedItemId(item.id)}
                      className="w-full text-left px-5 py-3 hover:bg-slate-50 transition flex items-start gap-3"
                    >
                      <Badge tone={STATUS_TONE[item.status] || 'neutral'}>{t(item.statusLabel)}</Badge>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-800">{t(item.name)}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {t(item.stage)} {t('· Responsable')} {t(item.responsible)}
                        </p>
                      </div>
                      <p className="text-xs text-slate-400">{formatDisplayDate(item.expectedDate)}</p>
                    </button>
                  ))
                )}
              </div>
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between">
                <span>{t(insightPanel.items.length)} {t('elemento(s) relacionados')}</span>
                {insightPanel.filters && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setFilters((prev) => ({ ...prev, ...insightPanel.filters }));
                      setInsightPanel(null);
                    }}
                  >{t('Aplicar filtros')}</Button>
                )}
              </div>
            </div>
          </div>
        )}

      {isResetConfirmOpen && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 max-w-md w-full space-y-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-danger-500" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{t('Restaurar matriz base')}</h3>
                  <p className="text-sm text-slate-500">{t(
                    'Esta acción revierte cualquier edición realizada en la matriz de permisos y contratos. ¿Deseas continuar?'
                  )}</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={() => setIsResetConfirmOpen(false)}>{t('Cancelar')}</Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    resetProjectStatusMatrix();
                    setSelectedItemId(null);
                    setIsResetConfirmOpen(false);
                    setFeedbackMessage('Se restauró la matriz base del proyecto.');
                    pushNotification({
                      title: 'Matriz restablecida',
                      message: 'Se restauraron los hitos de estado a su configuración inicial.',
                      severity: 'info'
                    });
                    setTimeout(() => setFeedbackMessage(''), 2500);
                  }}
                >{t('Restaurar')}</Button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default ProjectStatus;
